import { Api } from '@generated/api';
import { tokenManager } from '@services/auth/tokenManager';

// Extend Axios config type to include our retry flag and the per-request
// idempotency key. `_idempotencyKey` is persisted on the config so the
// 401→refresh→retry replay (which reuses the same config) reuses the SAME key.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retried?: boolean;
    _idempotencyKey?: string;
  }
}

// Auth/anon endpoints that must NOT carry an Idempotency-Key. These are either
// idempotent by nature or rely on their own dedupe semantics (registration uses
// email-uniqueness 409, not the idempotency library). Matched as anchored path
// suffixes against the request URL so e.g. `/login` does not also exclude
// `/login/verify-mfa` accidentally — both are listed explicitly.
const IDEMPOTENCY_EXCLUDED_PATHS = [
  '/api/auth/refresh',
  '/api/auth/login',
  '/api/auth/login/verify-mfa',
  '/api/auth/logout',
  '/api/auth/send-verification',
  '/api/auth/verify-email',
  '/api/auth/resend-verification',
  '/api/auth/register',
];

// HTTP methods that mutate server state and should carry an idempotency key.
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Returns true when the given request URL targets an excluded auth/anon endpoint.
 * Strips query/baseURL noise and compares against the path suffix so it matches
 * regardless of whether the URL is absolute or relative.
 */
function isIdempotencyExcluded(url: string): boolean {
  const path = url.split('?')[0];
  return IDEMPOTENCY_EXCLUDED_PATHS.some(
    (excluded) => path === excluded || path.endsWith(excluded),
  );
}

// Create configured API client
export const apiClient = new Api({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5203',
  timeout: 10000,
  // withCredentials ensures the httpOnly refresh-token cookie is included on
  // cross-origin requests (e.g. localhost:3000 → localhost:5203 in dev).
  // In production, same-hostname deployment means the cookie is sent regardless,
  // but this makes the client correct in all environments.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach access token to every request
apiClient.instance.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Idempotency: attach an Idempotency-Key to mutating requests that are not
    // excluded auth/anon endpoints. Skip GET/HEAD entirely (safe/idempotent).
    //
    // The key is persisted on the config via `??=` so the 401→refresh→retry
    // replay reuses the SAME key (mirrors the `_retried` pattern). If a caller
    // already set `_idempotencyKey` (stable per-action key from a service/page),
    // we respect it rather than minting a fresh one.
    const method = (config.method || '').toUpperCase();
    const url = config.url || '';
    if (MUTATING_METHODS.has(method) && !isIdempotencyExcluded(url)) {
      // A caller (service/page) may have supplied a STABLE per-action key as a
      // header on the request config. Seed `_idempotencyKey` from it so it both
      // persists across the retry and is treated as the authoritative key.
      const preset = config.headers['Idempotency-Key'];
      if (typeof preset === 'string' && preset.length > 0) {
        config._idempotencyKey ??= preset;
      }
      config._idempotencyKey ??= crypto.randomUUID();
      config.headers['Idempotency-Key'] = config._idempotencyKey;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor: on 401, attempt refresh + retry
apiClient.instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      // Guard: don't retry auth endpoints (would cause infinite loops)
      const url = originalRequest?.url || '';
      if (url.includes('/api/auth/refresh') || url.includes('/api/auth/login')) {
        return Promise.reject(error);
      }

      // Guard: don't retry a request we already retried
      if (originalRequest?._retried) {
        return Promise.reject(error);
      }
      originalRequest._retried = true;

      const refreshed = await tokenManager.attemptRefresh();

      if (refreshed) {
        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${tokenManager.getAccessToken()}`;
        return apiClient.instance.request(originalRequest);
      }

      // Refresh failed — `tokenManager.attemptRefresh()` has already dispatched
      // `session-expired` with the structured `errorCode` from the 401 body.
    }

    return Promise.reject(error);
  },
);

export default apiClient;
